import { AxiosError } from 'axios';
import camelCase from 'lodash/camelCase';
import startCase from 'lodash/startCase';

import { sendDatadogRumError } from 'utils/analytics/datadogRum';
import ProductCategory from 'modules/Documents/interfaces/ProductCategory.interface';
import collectiveApi from 'modules/common/collectiveApi';
import { Document } from 'modules/common/interfaces/documents';
import {
    DocumentHistoryEvent,
    DocumentHistoryQueryArgument,
    DocumentHistoryResponse,
} from 'modules/Documents/interfaces/documents';
import { Filters } from 'modules/Documents/FiltersContext/Filters.interface';
import DocumentType from 'modules/Documents/interfaces/DocumentType.interface';
import { localViews } from 'modules/Auth/AuthProvider/views';

export const fetchDocumentTypes = async () => {
    const { data } = await collectiveApi.get<DocumentType[]>('/documents/document-types/');
    return data;
};

export const fetchProductCategories = async (): Promise<ProductCategory[]> => {
    const { data } = await collectiveApi.get<ProductCategory[]>('/documents/product-categories/');

    const sortedData = data.sort((a, b) => {
        if (a.id === 'COMPANY' || b.id === 'OTHER') {
            return -1;
        }

        if ((b.id === 'PERSONAL' && a.id !== 'OTHER') || (a.id === 'TAX' && b.id === 'COMPLIANCE')) {
            return -1;
        }

        return a.id.localeCompare(b.id);
    });

    return sortedData;
};

export const fetchDocuments = async ({
    state: { isEmployeeView, member_uuid, ...filters },
    querySearch,
}: {
    state: Filters;
    querySearch: object;
}) => {
    if (isEmployeeView && !member_uuid) {
        return {};
    }

    try {
        const { page } = filters;
        const pageSize = filters.page_size || 10;

        const { data } = await collectiveApi.post(
            '/documents/search',
            {
                member_uuid,
                ...querySearch,
            },
            {
                params: {
                    page: page + 1,
                    page_size: pageSize,
                },
            }
        );

        return data;
    } catch (error) {
        sendDatadogRumError('documents.fetchDocumentUploadLink', error, {
            source: 'documents.fetchDocumentUploadLink',
        });
        // eslint-disable-next-line no-console
        console.error(error);
        return {};
    }
};

/** The name the backend signed into the link's download header, for callers that only hold a uuid. */
function fileNameFromSignedUrl(url: string): string | undefined {
    try {
        const disposition = new URL(url).searchParams.get('response-content-disposition');
        const encodedName = disposition?.match(/filename=([^;]+)/)?.[1];
        return encodedName ? decodeURIComponent(encodedName) : undefined;
    } catch {
        return undefined;
    }
}

/**
 * Fetch a document's bytes, named, so a viewer can tell its type even when the server labels it
 * generic binary. `memberId` confines an employee's lookup to that member: another member's
 * document is then a 404.
 */
export const fetchDocumentFile = async (isEmployeeView: boolean, document: Document, memberId?: number) => {
    const endpoint = isEmployeeView ? 'ms-member-documents' : 'member-documents';

    const {
        data: { url },
    } = await collectiveApi.get<{ url: string }>(`/documents/${endpoint}/${document.uuid}/download_url/`, {
        params: isEmployeeView && memberId !== undefined ? { member_id: memberId } : undefined,
    });

    const response = await fetch(url, {
        method: 'GET',
    });

    if (!response.ok) {
        // Otherwise S3's error page is handed back as though it were the document.
        throw new Error(`The document could not be loaded (status ${response.status}).`);
    }

    const blob = await response.blob();
    const data = new File([blob], document.file_name || fileNameFromSignedUrl(url) || '', { type: blob.type });

    return { document, data };
};

export const fetchDocumentUrl = async (documentUuid: string): Promise<string> => {
    const {
        data: { url },
    } = await collectiveApi.get<{ url: string }>(`/documents/member-documents/${documentUuid}/download_url/`);
    return url;
};

/**
 * Tell whether a member document is still reachable in the member's own document centre.
 *
 * Returns false only for a definitive "not there" — the retrieve endpoint is scoped to the
 * member's live, member-visible rows, so a 404 covers deleted, internal and someone else's. Any
 * other failure is inconclusive and rethrows rather than reporting the document as gone.
 */
export const confirmMemberDocumentAvailable = async (documentUuid: string): Promise<boolean> => {
    try {
        await collectiveApi.get(`/documents/member-documents/${documentUuid}/`);
        return true;
    } catch (error) {
        if ((error as AxiosError)?.response?.status === 404) {
            return false;
        }
        throw error;
    }
};

export const deleteDocument = async (document: Document) => {
    const isEmployee = localViews.isEmployee();
    const { data } = await collectiveApi.delete(
        `/documents/${isEmployee ? 'ms-member-documents' : 'member-documents'}/${document.uuid}/`
    );
    return data;
};

export const restoreDocument = async (document: Document) => {
    const { data } = await collectiveApi.post(`/documents/ms-member-documents/${document.uuid}/restore/`);
    return data;
};

export const fetchDocumentHistory = async (
    parameters: DocumentHistoryQueryArgument
): Promise<DocumentHistoryEvent[]> => {
    const toFriendlyName = (name: string) => startCase(camelCase(name.replace(/_id/g, '')));
    /**
     * We want to display one change per row, so we need to transform the data
     */
    const transformResponse = (data: DocumentHistoryEvent[]): DocumentHistoryEvent[] => {
        return data.flatMap((item) => {
            if (!item.changes) {
                return item;
            }

            const changeItems = Object.entries(item.changes).map(([key, value]) => ({
                ...item,
                changes: { [key]: value },
                comment: `${item.comment} ${toFriendlyName(key)}`,
            }));

            return changeItems;
        });
    };

    const { uuid, orderBy, orderDirection } = parameters;

    const { data }: DocumentHistoryResponse = await collectiveApi.get(
        `/documents/ms-member-documents/${uuid}/history/`,
        {
            params: {
                ordering: `${orderBy}:${orderDirection}`,
            },
        }
    );

    const mappedData = transformResponse(data);

    return mappedData;
};
